import React from 'react';
import { formatTimestamp, parseMessageContent, copyToClipboard as copyText } from './formatUtils';
import WeeklyReviewCard from './WeeklyReviewCard';
import { debugLog, debugWarn, debugError } from '../../utils/debug';
import { getCoachInitials } from './coachUtils';
import { haptic } from '../../utils/haptics';

// Track recently animated user messages to prevent double animations
const recentlyAnimatedMessages = new Set();
const ANIMATION_DEBOUNCE_TIME = 500; // 500ms debounce

// Component to render formatted message content
const FormattedMessage = ({ content, isUser }) => {
  const elements = parseMessageContent(content);
  
  const processText = (text) => {
    // Process bold text (**text** or *text*)
    let processed = text
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic text-gray-300">$1</em>')
      .replace(/_([^_]+)_/g, '<em class="italic text-gray-300">$1</em>');
    return processed;
  };
  
  return (
    <div className="leading-relaxed">
      {elements.map((element, index) => {
        switch (element.type) {
          case 'text':
            return (
              <span key={index}>
                {index > 0 && elements[index - 1].type === 'text' && <br />}
                <span 
                  className="break-words overflow-wrap-anywhere"
                  dangerouslySetInnerHTML={{ __html: element.content }}
                />
              </span>
            );
          
          case 'trainingPlan':
            return (
              <div key={index} className="space-y-3 my-2">
                {element.items.map((item, itemIndex) => (
                  <div 
                    key={itemIndex} 
                    className="border-l-2 border-blue-400/40 pl-3 py-1 space-y-1"
                  >
                    {/* Day and Workout Header */}
                    <div className="font-semibold text-base">
                      <span 
                        className="text-blue-300"
                        dangerouslySetInnerHTML={{ __html: processText(item.day) }}
                      />
                      {item.workout && (
                        <>
                          <span className="text-gray-300 mx-2">–</span>
                          <span 
                            className="text-gray-100"
                            dangerouslySetInnerHTML={{ __html: processText(item.workout) }}
                          />
                        </>
                      )}
                    </div>
                    
                    {/* Details */}
                    {item.details.map((detail, detailIndex) => (
                      <div 
                        key={detailIndex}
                        className="text-sm text-gray-300/90 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: processText(detail) }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            );
          
          case 'list':
            const ListComponent = element.listType === 'numbered' ? 'ol' : 'ul';
            return (
              <span key={index}>
                {index > 0 && <div className="h-2" />}
                <ListComponent 
                  className={`ml-4 space-y-1 ${
                    element.listType === 'numbered' 
                      ? 'list-decimal' 
                      : 'list-disc'
                  }`}
                >
                  {element.items.map((item, itemIndex) => (
                    <li 
                      key={itemIndex} 
                      className="leading-relaxed break-words overflow-wrap-anywhere"
                      dangerouslySetInnerHTML={{ __html: item }}
                    />
                  ))}
                </ListComponent>
                {index < elements.length - 1 && <div className="h-2" />}
              </span>
            );
          
          case 'break':
            return <div key={index} className="h-4" />;
          
          default:
            return null;
        }
      })}
    </div>
  );
};

const MessageBubble = ({ message, index, messages, user, onNavigateToSchedule, promptInfo, onSendMessage, onboardingData, setToastMessage, sameSenderAsPrev = false, sameSenderAsNext = false }) => {
  const isUser = message.is_user;
  const isSystem = message.role === 'system';
  const isScheduled = message.is_scheduled;
  const isFrequencyUpdate = message.is_frequency_update;
  const isRunTool = message.is_run_tool;
  const isRegimentTool = message.is_regimen_tool;
  const isError = message.is_error;
  
  // Check if this is a weekly review message
  const isWeeklyReview = message.content && message.content.includes('<weekly_review>') && message.content.includes('</weekly_review>');
  
  // State for copy functionality
  const [showCopyButton, setShowCopyButton] = React.useState(false);
  const [copySuccess, setCopySuccess] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const [showMessageMenu, setShowMessageMenu] = React.useState(false);
  const longPressTimer = React.useRef(null);
  const touchStartPos = React.useRef({ x: 0, y: 0 });
  const copyButtonRef = React.useRef(null);
  const menuRef = React.useRef(null);
  
  // Create a content-based key for debouncing user message animations
  const messageKey = isUser ? `user_${message.content.substring(0, 50)}` : message.id;
  
  // Detect if we're on a mobile device
  React.useEffect(() => {
    const checkIsMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768; // Tailwind's md breakpoint
      setIsMobile(isTouchDevice && isSmallScreen);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Check if this user message should be animated
  const shouldAnimate = React.useMemo(() => {
    if (!isUser && !isSystem) {
      return true; // Always animate AI messages
    }
    
    if (isSystem) {
      return true; // Always animate system messages
    }
    
    // For user messages, check if we've recently animated this content
    if (recentlyAnimatedMessages.has(messageKey)) {
      return false; // Skip animation for recently animated content
    }
    
    // Add to recently animated and set timeout to remove
    recentlyAnimatedMessages.add(messageKey);
    setTimeout(() => {
      recentlyAnimatedMessages.delete(messageKey);
    }, ANIMATION_DEBOUNCE_TIME);
    
    return true;
  }, [isUser, isSystem, messageKey]);

  // Long press handlers (only for mobile)
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    // Don't enable copy for system/scheduled messages as they're typically status messages
    if (isSystem || isScheduled || isFrequencyUpdate) return;
    
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    
    longPressTimer.current = setTimeout(() => {
      // Coach (non-user) messages: open the action menu (Copy + Report Issue).
      // User messages: just open the copy button affordance.
      if (!isUser) {
        setShowMessageMenu(true);
      } else {
        setShowCopyButton(true);
        setTimeout(() => {
          if (copyButtonRef.current) {
            copyButtonRef.current.focus();
          }
        }, 100);
      }
      haptic('medium');
    }, 500); // 500ms for long press
  };

  const handleTouchMove = (e) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
    
    // Cancel long press if user moves finger too much (more than 10px)
    if (deltaX > 10 || deltaY > 10) {
      clearTimeout(longPressTimer.current);
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    clearTimeout(longPressTimer.current);
  };

  // Copy bubble text via the shared util
  const copyToClipboard = async () => {
    const ok = await copyText(message.content.replace(/<[^>]*>/g, ''));
    if (ok) {
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
        setShowCopyButton(false);
      }, 2000);
    } else {
      setShowCopyButton(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (showCopyButton && e.key === 'Escape') {
      setShowCopyButton(false);
    }
  };

  // Close copy button when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCopyButton && copyButtonRef.current && !copyButtonRef.current.contains(e.target)) {
        setShowCopyButton(false);
      }
      if (showMessageMenu && menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMessageMenu(false);
      }
    };

    if (showCopyButton || showMessageMenu) {
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
      if (showCopyButton) {
        document.addEventListener('keydown', handleKeyDown);
      }
    }

    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCopyButton, showMessageMenu]);

  // Check if this is a schedule generation message
  const isScheduleGeneration = isSystem && (
    message.content.startsWith('🔄 Creating your training program') ||
    message.content.startsWith('🔄 Creating your custom training program') || // Keep for backward compatibility
    message.content.startsWith('✅ Creating your personalized training schedule') ||
    message.content.startsWith('✅ Your training schedule is ready') || // Works with or without !
    message.content.startsWith('✅ Onboarding complete') ||
    message.content.startsWith('✅ Your workouts enhanced with') ||
    message.content.startsWith('✅ Enhanced coaching details added') || // Keep for backward compatibility
    message.content.startsWith('✅ Your personalized training schedule has been created') ||
    message.content.startsWith('❌')
  );
  
  // 🎯 NEW: Check if Message 3 (schedule ready) exists later in the conversation
  // This allows us to stop shimmer animation when schedule is ready
  const hasScheduleReadyMessage = React.useMemo(() => {
    if (!messages || !Array.isArray(messages)) return false;
    
    // Only check messages that come AFTER this message (index > current index)
    return messages.some((msg, idx) => {
      if (idx <= index) return false;
      
      // Check if it's Message 3 (schedule ready message)
      return msg.role === 'system' && (
        msg.content.startsWith('✅ Your training schedule is ready') ||
        msg.content.includes('training schedule is ready') ||
        msg.content.includes('schedule is ready') ||
        msg.content.startsWith('✅ Onboarding complete') ||
        msg.content.startsWith('✅ Your personalized training schedule has been created')
      );
    });
  }, [messages, index]);
  
  // Only show shimmer if loading message AND schedule not ready yet
  const isScheduleGenerationLoading = isScheduleGeneration && 
    message.content.startsWith('🔄') && 
    !hasScheduleReadyMessage;
  const isScheduleInitialReady = isScheduleGeneration && (
    message.content.startsWith('✅ Your training schedule is ready') || // Works with or without !
    message.content.startsWith('✅ Onboarding complete') ||
    message.content.startsWith('✅ Your personalized training schedule has been created') ||
    message.content.includes('training schedule is ready') ||
    message.content.includes('schedule is ready') ||
    message.content.includes('personalized training schedule has been created')
  );
      const isScheduleEnhanced = isScheduleGeneration && (
        message.content.startsWith('✅ Your workouts enhanced with') ||
        message.content.startsWith('✅ Enhanced coaching details added') || // Keep for backward compatibility
        message.content.includes('workouts enhanced with') ||
        message.content.includes('Enhanced coaching details added') // Keep for backward compatibility
      );
  
  // Check if user has considerations that need follow-up
  
  // Use the preferences array to check for follow-up considerations
  const hasFollowUpConsiderations = onboardingData?.preferences && onboardingData.preferences.some(pref => 
    ['past_or_current_injury', 'training_in_another_sport', 'pregnant_or_postpartum', 'returning_after_long_break', 'let_me_talk_to_coach'].includes(pref)
  );
  
  const finalFollowUpCheck = hasFollowUpConsiderations;
  
  // Generate follow-up message that triggers coach to proactively address selected considerations
  const generateFollowUpMessage = () => {
    if (!onboardingData?.preferences || !Array.isArray(onboardingData.preferences)) {
      return "I'd like to discuss my considerations with you";
    }
    
    const selectedConsiderations = onboardingData.preferences;
    const considerationContext = [];
    
    if (selectedConsiderations.includes('past_or_current_injury')) {
      considerationContext.push("past or current injuries");
    }
    if (selectedConsiderations.includes('training_in_another_sport')) {
      considerationContext.push("training in another sport");
    }
    if (selectedConsiderations.includes('pregnant_or_postpartum')) {
      considerationContext.push("pregnancy or postpartum considerations");
    }
    if (selectedConsiderations.includes('returning_after_long_break')) {
      considerationContext.push("returning after a long break");
    }
    if (selectedConsiderations.includes('let_me_talk_to_coach')) {
      considerationContext.push("specific training concerns");
    }
    
    if (considerationContext.length === 0) {
      return "I'd like to discuss my considerations with you";
    }
    
    // Handle different lengths of considerations for better readability
    if (considerationContext.length === 1) {
      return `I'd like to talk about the ${considerationContext[0]} flagged during onboarding. Can you help me with this?`;
    } else if (considerationContext.length === 2) {
      return `I'd like to talk about the ${considerationContext.join(' and ')} flagged during onboarding. Can you help me with these?`;
    } else {
      // For 3+ items, use comma-separated format for better readability
      return `I'd like to talk about the considerations flagged during onboarding: ${considerationContext.join(', ')}. Can you help me with these?`;
    }
  };
  
  if (isScheduleInitialReady) {
    // Schedule ready message detected - checking for follow-up considerations
  }
  
  // Check if this is a reassessment message
  const isReassessment = isSystem && (
    message.content.startsWith('🔄 Resetting your profile for re-onboarding') ||
    message.content.startsWith('✅ Profile reset complete!') ||
    message.content.includes('❌ Failed to reset profile')
  );
  const isReassessmentLoading = isReassessment && message.content.startsWith('🔄');
  const isReassessmentSuccess = isReassessment && message.content.startsWith('✅ Profile reset complete!');

  // Handle successful reassessment by refreshing to trigger onboarding
  React.useEffect(() => {
    if (isReassessmentSuccess) {
      // Give user time to read the success message, then refresh to trigger onboarding
      const timer = setTimeout(() => {
        debugLog('Reassessment successful - refreshing to trigger onboarding');
        window.location.reload();
      }, 3000); // 3 second delay to show success message
      
      return () => clearTimeout(timer);
    }
  }, [isReassessmentSuccess]);
  
  // Handle weekly review messages
  if (isWeeklyReview && !isUser) {
    return (
      <div
        style={shouldAnimate ? { 
          animationDelay: `${Math.min(index * 0.05, 0.3)}s`,
          opacity: 0,
          animationName: 'slideInMessage',
          animationDuration: '0.4s',
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards'
        } : {
          opacity: 1
        }}
      >
        <WeeklyReviewCard 
          content={message.content} 
          timestamp={formatTimestamp(message.created_at)}
        />
      </div>
    );
  }

  // Handle system/scheduled messages with special styling
  if (isSystem || isScheduled || isFrequencyUpdate || isRunTool || isRegimentTool || isReassessment) {
    // Choose colors based on message type and error status
    let bgColor, textColor, borderColor, timestampColor, icon;
    
    if (isError || (isScheduleGeneration && message.content.startsWith('❌'))) {
      // Error styling
      bgColor = 'bg-red-900/30';
      textColor = 'text-red-200';
      borderColor = 'border-red-600/30';
      timestampColor = 'text-red-300/70';
      icon = '❌';
    } else if (isScheduleGeneration) {
      // Schedule generation styling (purple theme)
      bgColor = 'bg-purple-900/30';
      textColor = 'text-purple-200';
      borderColor = 'border-purple-600/30';
      timestampColor = 'text-purple-300/70';
      // More specific icons for different schedule generation stages
      if (isScheduleGenerationLoading) {
        icon = ''; // Don't add icon - message content already has 🔄
      } else if (isScheduleInitialReady) {
        icon = ''; // Don't add icon - message content already has ✅
      } else if (isScheduleEnhanced) {
        icon = ''; // Don't add icon - message content already has ✅ and 🎯
      } else {
        icon = '✅'; // Checkmark for final completion
      }
    } else if (isReassessment) {
      // Reassessment styling (orange theme for reset/restart actions)
      bgColor = 'bg-orange-900/30';
      textColor = 'text-orange-200';
      borderColor = 'border-orange-600/30';
      timestampColor = 'text-orange-300/70';
      // More specific icons for different reassessment stages
      if (isReassessmentLoading) {
        icon = '🔄';
      } else if (isReassessmentSuccess) {
        icon = '🔄'; // Restart icon for successful reset
      } else {
        icon = '⚙️'; // Settings icon for profile changes
      }
    } else if (isRegimentTool) {
      // Regimen tool styling (teal theme for training planning)
      bgColor = 'bg-teal-900/30';
      textColor = 'text-teal-200';
      borderColor = 'border-teal-600/30';
      timestampColor = 'text-teal-300/70';
      icon = message.is_loading ? '⏳' : '🗓️';
    } else if (isRunTool) {
      // Run tool styling (green theme for fitness/health)
      bgColor = 'bg-green-900/30';
      textColor = 'text-green-200';
      borderColor = 'border-green-600/30';
      timestampColor = 'text-green-300/70';
      icon = message.is_loading ? '⏳' : '🏃‍♂️';
    } else if (isFrequencyUpdate) {
      // Frequency update styling (purple theme)
      bgColor = 'bg-purple-900/30';
      textColor = 'text-purple-200';
      borderColor = 'border-purple-600/30';
      timestampColor = 'text-purple-300/70';
      icon = message.is_loading ? '⏳' : '⚙️';
    } else {
      // Default system/scheduled styling (blue theme)
      bgColor = 'bg-blue-900/30';
      textColor = 'text-blue-200';
      borderColor = 'border-blue-600/30';
      timestampColor = 'text-blue-300/70';
      icon = message.is_loading ? '⏳' : '📅';
    }
    
    return (
      <div
        className="flex justify-center mb-4"
        style={shouldAnimate ? { 
          animationDelay: `${Math.min(index * 0.05, 0.3)}s`,
          opacity: 0,
          animationName: 'slideInMessage',
          animationDuration: '0.4s',
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards'
        } : {
          opacity: 1
        }}
      >
        <div className={`${bgColor} ${textColor} px-4 py-3 rounded-xl ${isRegimentTool ? `border-l border-r border-b ${borderColor}` : `border ${borderColor}`} w-[85%] relative overflow-hidden`}>
          {/* Loading animation overlay for run tools and schedule generation */}
          {((isRunTool || isRegimentTool) && message.is_loading) || isScheduleGenerationLoading ? (
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer"></div>
          ) : null}
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              {icon && (
                <span className={`text-lg ${isScheduleGenerationLoading ? 'animate-spin' : ''}`}>{icon}</span>
              )}
              {!isScheduleGeneration && (
                <span className="text-sm font-medium">
                  {isRegimentTool ? 'Training Regimens' :
                   isRunTool ? 'Run Tracking' : 
                   isFrequencyUpdate ? 'Messaging Settings' : 
                   isScheduled ? 'Scheduled Message' : 'System'}
                </span>
              )}
              {/* Show count badge for grouped messages */}
              {message.is_grouped && message.grouped_count > 1 && (
                <span className="bg-current/20 text-xs px-2 py-0.5 rounded-full font-medium">
                  {message.grouped_count} actions
                </span>
              )}
            </div>
            
            <div className="text-sm leading-relaxed">
              <FormattedMessage content={message.content} isUser={false} />
              
              {/* 🎯 NEW: Add buttons for initial schedule ready state - two-line layout */}
              {isScheduleInitialReady && (
                <div className="mt-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => {
                          // Mark schedule as clicked when user navigates via this button
                          localStorage.setItem('schedule_button_clicked', 'true');
                          onNavigateToSchedule && onNavigateToSchedule();
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                      >
                        <span>📅</span>
                        View Your Schedule
                      </button>
                      
                      {/* Follow-up button for users with considerations */}
                      {finalFollowUpCheck && onSendMessage && (
                        <button
                          onClick={() => onSendMessage(generateFollowUpMessage())}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                        >
                          <span>💬</span>
                          Follow-up with Coach
                        </button>
                      )}
                    </div>
                  </div>
                  {isScheduleInitialReady && (
                    <p className="text-xs text-gray-400 mt-2">
                      💡 Training schedule is being enhanced with form cues and pacing strategies.
                    </p>
                  )}
                </div>
              )}
              
              {/* Show breakdown for grouped messages */}
              {message.is_grouped && message.grouped_count > 1 && (
                <div className="mt-3 p-3 bg-current/10 rounded-lg">
                  <div className="text-xs font-medium mb-2 opacity-75">Action Summary:</div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                      <span>{message.success_count} successful</span>
                    </div>
                    {message.error_count > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                        <span>{message.error_count} failed</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 opacity-60">
                      <span className="w-2 h-2 bg-current rounded-full"></span>
                      <span>{message.grouped_count} total</span>
                    </div>
                  </div>
                </div>
              )}
              
            </div>
            
            {/* Detailed information card for run tool messages with data */}
            {(isRunTool || isRegimentTool) && !message.is_loading && !isError && (message.schedule_details || message.run_details) && (
              <div className="mt-3 p-4 bg-black/20 rounded-lg border border-current/20 cursor-pointer hover:bg-black/30 hover:border-current/40 active:bg-black/40 transition-all duration-200 transform active:scale-[0.98] hover:shadow-lg" 
                   onClick={() => {
                     if (message.schedule_details) {
                       onNavigateToSchedule && onNavigateToSchedule(message.schedule_details);
                     }
                   }}>
                {message.schedule_details && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold uppercase tracking-wide opacity-75">
                          {message.schedule_details.status === 'deleted' ? 'Deleted Schedule' : 'Schedule Details'}
                        </span>
                        <span className="text-xs bg-current/20 px-2 py-0.5 rounded-full">ID: {message.schedule_details.id || 'N/A'}</span>
                      </div>
                      <div className="text-xs opacity-50 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Click to view
                      </div>
                    </div>
                    
                    {/* Main schedule info */}
                    <div className="mb-3">
                      <div className="text-sm font-medium mb-1">
                        {(() => {
                          const freq = message.schedule_details.frequency;
                          const interval = message.schedule_details.interval_value || 1;
                          const daysOfWeek = message.schedule_details.days_of_week;
                          
                          if (freq === 'weekly' && daysOfWeek) {
                            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                            const selectedDays = daysOfWeek.split(',').map(d => dayNames[parseInt(d)]).filter(Boolean);
                            return interval === 1 ? 
                              `Weekly on ${selectedDays.join(', ')}` : 
                              `Every ${interval} weeks on ${selectedDays.join(', ')}`;
                          } else if (freq === 'daily') {
                            return interval === 1 ? 'Daily' : `Every ${interval} days`;
                          } else if (freq === 'monthly') {
                            return interval === 1 ? 'Monthly' : `Every ${interval} months`;
                          }
                          return freq || 'Custom Schedule';
                        })()}
                      </div>
                      
                      {/* Time and duration - only show if we have the data */}
                      {(message.schedule_details.start_time || message.schedule_details.duration_minutes || message.schedule_details.distance_km) && (
                        <div className="flex items-center gap-4 text-xs opacity-75 flex-wrap">
                          {message.schedule_details.start_time && (
                            <div className="flex items-center gap-1">
                              <span>🕒</span>
                              <span>
                                {(() => {
                                  try {
                                    // Parse the ISO string and convert to local time
                                    const date = new Date(message.schedule_details.start_time);
                                    // Check if the date is valid
                                    if (isNaN(date.getTime())) {
                                      return 'Time set';
                                    }
                                    return date.toLocaleTimeString('en-US', { 
                                      hour: 'numeric', 
                                      minute: '2-digit',
                                      hour12: true,
                                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                                    });
                                  } catch (e) {
                                    debugWarn('Error parsing start_time:', e);
                                    return 'Time set';
                                  }
                                })()}
                              </span>
                            </div>
                          )}
                          {message.schedule_details.duration_minutes && (
                            <div className="flex items-center gap-1">
                              <span>⏱️</span>
                              <span>{message.schedule_details.duration_minutes} minutes</span>
                            </div>
                          )}
                          {message.schedule_details.distance_km && (
                            <div className="flex items-center gap-1">
                              <span>📏</span>
                              <span>{message.schedule_details.distance_km}km</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Additional details grid - adjust for deleted schedules */}
                    <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                      <div className="space-y-1">
                        <div className="opacity-75">Status:</div>
                        <div className="flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${
                            message.schedule_details.status === 'active' ? 'bg-green-400' : 
                            message.schedule_details.status === 'deleted' ? 'bg-red-400' : 
                            'bg-gray-400'
                          }`}></span>
                          <span className="font-medium capitalize">{message.schedule_details.status || 'active'}</span>
                        </div>
                      </div>
                      
                      {/* Show deleted sessions count for deleted schedules */}
                      {message.schedule_details.status === 'deleted' && message.deleted_planned_runs && (
                        <div className="space-y-1">
                          <div className="opacity-75">Removed:</div>
                          <div className="font-medium">
                            {message.deleted_planned_runs} training sessions
                          </div>
                        </div>
                      )}
                      
                      {/* Show end condition for active schedules */}
                      {message.schedule_details.status !== 'deleted' && message.schedule_details.end_condition && (
                        <div className="space-y-1">
                          <div className="opacity-75">Ends:</div>
                          <div className="font-medium">
                            {message.schedule_details.end_condition === 'count' ? 
                              `After ${message.schedule_details.max_occurrences || '?'} sessions` :
                              message.schedule_details.end_date ? 
                                (() => {
                                  try {
                                    // Parse the ISO string and convert to local time
                                    const date = new Date(message.schedule_details.end_date);
                                    // Check if the date is valid
                                    if (isNaN(date.getTime())) {
                                      return 'Date set';
                                    }
                                    return date.toLocaleDateString('en-US', {
                                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                                    });
                                  } catch (e) {
                                    debugWarn('Error parsing end_date:', e);
                                    return 'Date set';
                                  }
                                })() :
                                'Date specified'
                            }
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Notes */}
                    {message.schedule_details.notes && (
                      <div className="mb-3 p-2 bg-current/10 rounded text-xs">
                        <div className="opacity-75 mb-1">Notes:</div>
                        <div className="opacity-90">{message.schedule_details.notes}</div>
                      </div>
                    )}
                    
                    {/* Training sessions created indicator for new schedules */}
                    {message.runs_created && message.schedule_details.status !== 'deleted' && (
                      <div className="flex items-center justify-between pt-3 border-t border-current/20">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">✨</span>
                          <span className="text-xs font-medium">{message.runs_created} training sessions created</span>
                        </div>
                        <div className="text-xs opacity-50">Ready to go!</div>
                      </div>
                    )}
                    
                    {/* Deletion confirmation for deleted schedules */}
                    {message.schedule_details.status === 'deleted' && (
                      <div className="flex items-center justify-between pt-3 border-t border-current/20">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">🗑️</span>
                          <span className="text-xs font-medium">Schedule permanently removed</span>
                        </div>
                        <div className="text-xs opacity-50">All clear!</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Success indicator for tool messages without detailed data */}
            {/* Removed unnecessary "Done" message - the status message is self-explanatory */}
          </div>
          
          <div className="mt-3 pt-2 border-t border-current/20">
            <span className={`text-xs ${timestampColor}`}>
              {formatTimestamp(message.created_at)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className={`flex items-end gap-2 group ${sameSenderAsPrev ? 'mb-1' : 'mb-6'} ${
        isUser ? 'justify-end' : 'justify-start'
      }`}
    >
      {/* Coach Avatar — hide on consecutive bubbles from same sender (avatar reserves space) */}
      {!isUser && (
        <div className={`relative ${sameSenderAsPrev ? 'invisible' : ''}`}>
          <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0 transition-transform duration-200 active:scale-95">
            <span className="text-white text-xs font-medium">
              {(() => {
                const promptData = message.prompt_set || promptInfo;
                return getCoachInitials(promptData);
              })()}
            </span>
          </div>
        </div>
      )}

      {/* Message Content */}
      <div
        className={`relative max-w-[85%] min-w-0 ${
          isUser ? 'order-first' : ''
        }`}
        style={shouldAnimate ? { 
          animationDelay: message.temp ? '0s' : `${Math.min(index * 0.05, 0.3)}s`,
          opacity: 0,
          animationName: 'slideInMessage',
          animationDuration: '0.4s',
          animationTimingFunction: 'ease-out',
          animationFillMode: 'forwards'
        } : {
          opacity: 1 // No animation, just show immediately
        }}
      >
        {/* Message bubble — collapse tail on continuation bubbles in a cluster */}
        <div
          className={`px-4 py-2 rounded-2xl transition-all duration-200 max-w-full ${
            isMobile ? 'select-none' : ''
          } ${
            isUser
              ? `bg-blue-500 text-white shadow-sm hover:shadow-md ${sameSenderAsNext ? '' : 'rounded-br-sm'}`
              : `bg-gray-700 text-gray-100 shadow-sm hover:shadow-md ${sameSenderAsNext ? '' : 'rounded-bl-sm'}`
          } ${showCopyButton ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={isMobile ? {
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          } : {}}
        >
          {/* Message text */}
          <div className="text-[15px] break-words overflow-wrap-anywhere">
            <FormattedMessage content={message.content} isUser={isUser} />
          </div>
          
          {/* Timestamp + Copy button — only on the last bubble of a same-sender cluster */}
          {!sameSenderAsNext && (
          <div className="flex items-center justify-between mt-1">
            <span className={`text-xs ${
              isUser
                ? 'text-blue-100/80'
                : 'text-gray-400'
            }`}>
              {formatTimestamp(message.created_at)}
            </span>
            <div className="flex items-center gap-2">
              {/* Copy button — desktop hover only; mobile uses long-press */}
              <button
              onClick={async (e) => {
                e.stopPropagation();
                const ok = await copyText(message.content.replace(/<[^>]*>/g, '').trim());
                if (ok) {
                  setCopySuccess(true);
                  setTimeout(() => setCopySuccess(false), 1500);
                }
                haptic('selection');
              }}
              className="ml-3 opacity-0 md:group-hover:opacity-100 active:opacity-100 transition-opacity duration-200 hover:bg-white/10 rounded-lg p-1.5 -mr-1.5"
              title={copySuccess ? "Copied!" : "Copy message"}
            >
              {copySuccess ? (
                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            {/* Three dots menu — only for coach messages */}
            {!isUser && !isSystem && !isScheduled && !isFrequencyUpdate && (
              <div className="relative">
                <button
                  ref={menuRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMessageMenu(!showMessageMenu);
                    import("../../utils/haptics").then(m => m.haptic("selection")).catch(() => {});
                  }}
                  className="text-xs text-gray-500 hover:text-gray-300 opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 p-1 -mr-1"
                  title="More options"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </button>
                {showMessageMenu && (
                  <div 
                    className="absolute right-0 bottom-full mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50 min-w-[160px]"
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowMessageMenu(false);
                        window.dispatchEvent(
                          new CustomEvent('open-feedback-form', {
                            detail: {
                              defaultType: 'bug',
                              defaultTitle: `Coach response issue - ${formatTimestamp(message.created_at)}`,
                              contextSource: 'chat-message',
                            }
                          })
                        );
                        import("../../utils/haptics").then(m => m.haptic("selection")).catch(() => {});
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-500 hover:text-gray-300 active:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors duration-200"
                    >
                      Report Issue
                    </button>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
          )}
        </div>

        {/* Copy Button */}
        {showCopyButton && isMobile && (
          <div 
            className={`absolute ${
              isUser ? 'left-0 ml-2' : 'right-0 mr-2'
            } top-1/2 transform -translate-y-1/2 z-10`}
          >
            <button
              ref={copyButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  copyToClipboard();
                }
              }}
              className={`
                px-3 py-1.5 rounded-lg shadow-lg text-xs font-medium
                transition-all duration-200 transform focus:outline-none focus:ring-2 focus:ring-blue-500
                ${copySuccess 
                  ? 'bg-green-500 text-white scale-105' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 active:scale-95'
                }
              `}
              aria-label={copySuccess ? 'Message copied' : 'Copy message'}
            >
              {copySuccess ? (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* User Avatar */}
      {isUser && (
        <div className="relative">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 transition-transform duration-200 active:scale-95">
            <span className="text-white text-xs font-medium">
              {user?.firstName?.charAt(0) || 'U'}{user?.lastName?.charAt(0) || ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;